import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  FileCheck, 
  Send, 
  BarChart3, 
  DollarSign, 
  ArrowRight,
  Check,
  Globe,
  Clock,
  Shield,
  Users,
  Film,
  Palette,
  Video
} from 'lucide-react';
import { PlatformLogos } from '../components/PlatformLogos';
import { PageSEO } from '../components/SEO';

const steps = [
  {
    icon: Upload,
    step: '01',
    title: 'SUBMIT YOUR FILM',
    description: 'Complete our submission form with your film details, screener link, and any festival history or awards. We accept completed films and documentaries only—no scripts or projects in development.',
  },
  {
    icon: FileCheck,
    step: '02',
    title: 'CURATORIAL REVIEW',
    description: 'Our team carefully evaluates every submission. We assess the story, performances, execution, and whether there\'s a clear target audience. Not every film is accepted—we curate a quality catalogue.',
  },
  {
    icon: Send,
    step: '03',
    title: 'ONBOARDING & DELIVERY',
    description: 'Once accepted, we handle poster production, trailer editing, technical encoding, metadata, and delivery to all platforms. Expect around 90 days from acceptance to going live.',
  },
  {
    icon: BarChart3,
    step: '04',
    title: 'TRACK & EARN',
    description: 'Monitor your views and revenue through transparent monthly reports. Receive 70% of all earnings with zero hidden fees.',
  },
];

const benefits = [
  { icon: Globe, title: 'GLOBAL DISTRIBUTION', description: 'Reach viewers in 190+ countries across all major streaming platforms.' },
  { icon: Clock, title: 'AROUND 90 DAYS', description: 'From acceptance to live on platforms in around 90 days—we handle everything.' },
  { icon: DollarSign, title: '70% REVENUE SHARE', description: 'Keep the lion\'s share of your earnings. You made the film, you deserve the profit.' },
  { icon: Shield, title: 'RIGHTS PROTECTION', description: 'We handle DRM and content protection across all platforms.' },
];

const included = [
  'Professional poster design & production',
  'Trailer editing & formatting',
  'Platform encoding & transcoding',
  'Metadata optimisation for discoverability',
  'Thumbnail & artwork formatting',
  'Closed caption/subtitle processing',
  'Content delivery to all platforms',
  'Monthly revenue reporting',
  'Direct deposit payments',
  'Ongoing performance analytics',
];

export default function Distribution() {
  return (
    <div data-testid="distribution-page" className="min-h-screen pt-20">
      <PageSEO page="distribution" />
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_filmmaker-profits/artifacts/96ur1eac_older%20gods%20thumnail%20fog%20monster.jpg"
            alt="Scene from Older Gods - VOD film distribution services in the UK"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/80 to-neutral-950/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-6">
              VOD DISTRIBUTION
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              GET YOUR FILM<br />
              <span className="text-orange-500">EVERYWHERE</span>
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-xl">
              We don't just work with anyone—we curate a catalogue of quality films. If your film has the story, performances, and execution, we'll get it in front of global audiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-20 bg-neutral-900/50 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-8 block">
            GET YOUR FILM ON THE FOLLOWING PLATFORMS...
          </span>
          <PlatformLogos />
          
          {/* Pitch Platforms */}
          <div className="mt-16 pt-12 border-t border-white/10">
            <span className="text-neutral-400 font-sans text-sm tracking-[0.2em] uppercase mb-6 block">
              WE ALSO PITCH YOUR FILM TO...
            </span>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 mb-6">
              <span className="font-display text-xl md:text-2xl text-white/40 uppercase tracking-wider">Netflix</span>
              <span className="font-display text-xl md:text-2xl text-white/40 uppercase tracking-wider">HBO Max</span>
              <span className="font-display text-xl md:text-2xl text-white/40 uppercase tracking-wider">Shudder</span>
              <span className="font-display text-xl md:text-2xl text-white/40 uppercase tracking-wider">Starz</span>
            </div>
            <p className="text-neutral-500 text-sm max-w-2xl mx-auto">
              These platforms are not guaranteed and depend on the platform's interest in your film. We pitch on your behalf and negotiate deals if they want to acquire.
            </p>
          </div>
        </div>
      </section>

      {/* Curated Catalogue Section */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                A CURATED CATALOGUE
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
                WHAT WE ARE LOOKING FOR
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-6">
                We're selective about the films we represent. When you submit your film, our team evaluates every aspect—the idea, the story, the performances, the execution, and whether there's an obvious target audience who will connect with your work.
              </p>
              <p className="text-neutral-400 text-lg leading-relaxed mb-6">
                This isn't about gatekeeping—it's about quality. We believe that a curated catalogue benefits everyone. Audiences trust our recommendations, and filmmakers benefit from being part of a collection that stands for something.
              </p>
              <p className="text-neutral-300 text-lg leading-relaxed font-medium">
                If your film is accepted, you're not just getting distribution—you're joining a community of filmmakers who support each other's work and share in each other's success.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 gap-6"
            >
              <div className="p-6 border border-white/10 bg-neutral-900/50">
                <Users className="w-10 h-10 text-orange-500 mb-4" />
                <h3 className="font-display text-xl tracking-wider text-white mb-2 uppercase">JOIN A COMMUNITY</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  Our filmmakers support each other—sharing audiences, cross-promoting, and building something bigger together.
                </p>
              </div>
              <div className="p-6 border border-white/10 bg-neutral-900/50">
                <Film className="w-10 h-10 text-orange-500 mb-4" />
                <h3 className="font-display text-xl tracking-wider text-white mb-2 uppercase">QUALITY OVER QUANTITY</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">
                  We'd rather have 10 great films than 500 forgettable ones. Every film in our catalogue earned its place.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              THE PROCESS
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white">
              HOW IT WORKS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 border border-white/10 bg-neutral-900/50 hover:bg-neutral-900 transition-colors relative group"
              >
                <span className="font-display text-6xl text-white/5 absolute top-4 right-4 group-hover:text-orange-500/10 transition-colors">
                  {step.step}
                </span>
                <step.icon className="w-10 h-10 text-orange-500 mb-6" />
                <h3 className="font-display text-xl tracking-wider text-white mb-4 uppercase">{step.title}</h3>
                <p className="text-neutral-400 leading-relaxed text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included - Poster & Trailer */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              INCLUDED IN OUR SERVICE
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
              POSTER & TRAILER PRODUCTION
            </h2>
            <p className="text-neutral-400 text-lg max-w-3xl mx-auto">
              Don't have professional marketing materials? No problem. Our 30% commission includes poster design and trailer editing—everything you need to look great on every platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 border border-orange-500/30 bg-orange-500/5"
            >
              <Palette className="w-12 h-12 text-orange-500 mb-6" />
              <h3 className="font-display text-2xl tracking-wider text-white mb-4 uppercase">POSTER PRODUCTION</h3>
              <p className="text-neutral-300 leading-relaxed mb-4">
                Our design team creates professional key art that captures the essence of your film. We produce posters optimised for every platform—from portrait formats for streaming services to landscape banners for social media.
              </p>
              <ul className="space-y-2 text-neutral-400 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Main theatrical-style poster</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Platform-specific formats</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Social media assets</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 border border-orange-500/30 bg-orange-500/5"
            >
              <Video className="w-12 h-12 text-orange-500 mb-6" />
              <h3 className="font-display text-2xl tracking-wider text-white mb-4 uppercase">TRAILER EDITING</h3>
              <p className="text-neutral-300 leading-relaxed mb-4">
                A great trailer can make or break your film's success. Our editors craft compelling trailers that hook viewers and drive them to watch—formatted perfectly for every platform's requirements.
              </p>
              <ul className="space-y-2 text-neutral-400 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Official trailer (2-3 minutes)</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Short-form cuts for social</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Platform-optimised encoding</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                WHY LBR
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
                DISTRIBUTION THAT WORKS FOR YOU
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                The film industry has been broken for too long. Filmmakers work for years on their projects only to see the majority of profits disappear into distributor pockets. We're changing that.
              </p>
              <p className="text-neutral-400 text-lg leading-relaxed">
                With LBR Distribution, you keep 70% of all revenue. No setup fees. No hidden costs. No surprise deductions. Just transparent, monthly payments directly to your account.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 border border-white/10 bg-neutral-900/50"
                >
                  <benefit.icon className="w-8 h-8 text-orange-500 mb-4" />
                  <h3 className="font-display text-lg tracking-wider text-white mb-2 uppercase">{benefit.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {included.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-orange-600/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-orange-500" />
                    </div>
                    <span className="text-neutral-300 text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                FULL SERVICE
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
                EVERYTHING INCLUDED
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                Our 30% commission covers the complete distribution process—including poster design and trailer editing. There are no additional fees, no per-platform charges, and no unexpected costs down the line.
              </p>
              <div className="p-6 border border-orange-500/30 bg-orange-500/5">
                <div className="flex items-center justify-between">
                  <span className="text-white font-display text-xl uppercase tracking-wider">OUR COMMISSION</span>
                  <span className="text-orange-500 font-display text-4xl">30%</span>
                </div>
                <p className="text-neutral-400 text-sm mt-2">That's it. No hidden fees. Poster & trailer included. Ever.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-900/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl tracking-tight uppercase text-white mb-6">
              THINK YOUR FILM HAS WHAT IT TAKES?
            </h2>
            <p className="text-neutral-300 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Submit your completed film for consideration. If it's got the story, the performances, and a clear audience—we want to hear from you.
            </p>
            <Link
              to="/submit"
              data-testid="distribution-cta-btn"
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
            >
              SUBMIT YOUR FILM
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
